import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, ActivityLog, GPSLocation, Permissions, AppNotification } from '../types';
import { supabaseAdmin } from '../lib/supabase';
const NOTIFS_STORAGE_KEY = 'ventastrack_app_notifications';
function loadStoredNotifications(): AppNotification[] {
  try {
    const stored = localStorage.getItem(NOTIFS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((n: any) => ({
        ...n,
        timestamp: new Date(n.timestamp)
      }));
    }
  } catch {}
  return [];
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  availableUsers: User[];
  login: (emailOrId: string, password?: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  logout: () => void;
  activityLogs: ActivityLog[];
  addActivityLog: (action: string, details: string) => void;
  notifications: AppNotification[];
  addNotification: (type: AppNotification['type'], title: string, message: string) => void;
  markNotificationsRead: () => void;
  isLoading: boolean;
  refreshUsers: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Helper para mapear perfil de DB a tipo User
function mapProfile(row: any, roleMap: Record<string, any>): User {
  const role = roleMap[row.rol_id] || {
    nombre: 'Desconocido',
    permisos: {}
  };
  return {
    id: row.id,
    nombre: row.nombre,
    email: row.email || '',
    password: row.password,
    rolId: row.rol_id,
    rolNombre: role.nombre,
    permisos: role.permisos as Permissions,
    activo: row.activo,
    fechaCreacion: new Date(row.fecha_creacion)
  };
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>(loadStoredNotifications);
  const [isLoading, setIsLoading] = useState(true);
  // ── Inactivity timeout: 50 minutes ────────────────────────────────────────
  const INACTIVITY_TIMEOUT_MS = 50 * 60 * 1000; // 50 minutes
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      // Auto-logout on inactivity — show expiry modal instead of silent reload
      setUser(null);
      localStorage.removeItem('ventastrack_user_id');
      localStorage.removeItem('ventastrack_user_cache');
      setSessionExpired(true);
    }, INACTIVITY_TIMEOUT_MS);
  }, []);
  useEffect(() => {
    if (!user) {
      // Not authenticated — clear timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }
    // Start tracking inactivity
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'mousedown'];
    const handleActivity = () => resetInactivityTimer();
    activityEvents.forEach(event => window.addEventListener(event, handleActivity, {
      passive: true
    }));
    // Start initial timer
    resetInactivityTimer();
    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer]);
  const getCurrentLocation = (): Promise<GPSLocation | null> => {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      // Safety timeout in case geolocation hangs
      const timeout = setTimeout(() => resolve(null), 5000);
      navigator.geolocation.getCurrentPosition(pos => {
        clearTimeout(timeout);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          timestamp: new Date()
        });
      }, () => {
        clearTimeout(timeout);
        resolve(null);
      }, {
        enableHighAccuracy: false,
        timeout: 4000,
        maximumAge: 60000
      });
    });
  };
  // Cargar usuarios y roles
  const loadUsersAndRoles = async () => {
    try {
      // 1. Cargar roles
      const {
        data: rolesData,
        error: rolesError
      } = await supabaseAdmin.from('roles').select('*');
      if (rolesError) throw rolesError;
      const roleMap: Record<string, any> = {};
      rolesData?.forEach(r => {
        roleMap[r.id] = r;
      });
      // 2. Cargar perfiles
      const {
        data: profilesData,
        error: profilesError
      } = await supabaseAdmin.from('profiles').select('*').eq('activo', true).order('nombre');
      if (profilesError) throw profilesError;
      if (profilesData) {
        const mappedUsers = profilesData.map(p => mapProfile(p, roleMap));
        setAvailableUsers(mappedUsers);
      }
    } catch (err) {
      console.error('Error cargando usuarios/roles:', err);
      // Fallback básico si falla DB
      setAvailableUsers([]);
    }
  };
  const loadActivityLogs = async (userId: string, isAdmin: boolean) => {
    try {
      let query = supabaseAdmin.from('activity_logs').select('id,user_id,user_name,action,timestamp,details').order('timestamp', {
        ascending: false
      }).limit(5000); // Load all activity logs
      if (!isAdmin) query = query.eq('user_id', userId);
      const {
        data
      } = await query;
      if (data) {
        setActivityLogs(data.map(log => ({
          id: log.id,
          userId: log.user_id,
          userName: log.user_name,
          action: log.action,
          timestamp: new Date(log.timestamp),
          location: log.location ? {
            ...log.location,
            timestamp: new Date(log.location.timestamp)
          } : null,
          details: log.details
        })));
      }
    } catch (err) {
      console.error('Error loading logs:', err);
    }
  };
  useEffect(() => {
    // Safety timeout: if init hangs for more than 8 seconds, force loading to stop
    const safetyTimer = setTimeout(() => {
      console.warn('[Auth] Safety timeout — forcing loading to stop');
      setIsLoading(false);
    }, 8000);
    const init = async () => {
      try {
        // ── Instant restore from localStorage cache ──────────────────────────
        const savedUserId = localStorage.getItem('ventastrack_user_id');
        const cachedUserJson = localStorage.getItem('ventastrack_user_cache');
        if (savedUserId && cachedUserJson) {
          try {
            const cachedUser = JSON.parse(cachedUserJson);
            if (cachedUser && cachedUser.id === savedUserId && cachedUser.activo) {
              // Restore user instantly from cache — UI unlocks immediately
              setUser({
                ...cachedUser,
                fechaCreacion: new Date(cachedUser.fechaCreacion)
              });
              setIsLoading(false); // Unlock UI immediately
            }
          } catch {
            // Bad cache — ignore
          }
        }
        // ── Load fresh data from DB (in parallel) ────────────────────────────
        const [rolesResult, profilesResult] = await Promise.all([supabaseAdmin.from('roles').select('*'), supabaseAdmin.from('profiles').select('*').eq('activo', true).order('nombre')]);
        const roleMap: Record<string, any> = {};
        rolesResult.data?.forEach(r => {
          roleMap[r.id] = r;
        });
        if (profilesResult.data) {
          const mappedUsers = profilesResult.data.map(p => mapProfile(p, roleMap));
          setAvailableUsers(mappedUsers);
        }
        // ── Restore/validate session from DB ─────────────────────────────────
        if (savedUserId) {
          const userData = profilesResult.data?.find((p: any) => p.id === savedUserId);
          if (userData && userData.activo) {
            const userObj = mapProfile(userData, roleMap);
            setUser(userObj);
            // Cache user for instant restore on next load
            localStorage.setItem('ventastrack_user_cache', JSON.stringify(userObj));
            if (userData.avatar_url) {
              localStorage.setItem(`ventastrack_avatar_${userObj.id}`, userData.avatar_url);
            }
            window.dispatchEvent(new Event('avatar-updated'));
            // Load activity logs but don't block on it
            loadActivityLogs(userObj.id, userObj.rolNombre === 'Administrador').catch(() => {});
          } else {
            // User not found or deactivated — clear session
            setUser(null);
            localStorage.removeItem('ventastrack_user_id');
            localStorage.removeItem('ventastrack_user_cache');
          }
        }
      } catch (err) {
        console.error('Error inicializando sesión:', err);
      } finally {
        clearTimeout(safetyTimer);
        setIsLoading(false);
      }
    };
    init();
    return () => clearTimeout(safetyTimer);
  }, []);
  // ── Auto-refresh user permissions via event from AppContext ──────────────
  // AppContext already subscribes to roles/profiles changes via realtime.
  // We listen for the 'supabase-change' custom event to avoid duplicate channels.
  useEffect(() => {
    if (!user) return;
    const refreshCurrentUserPermissions = async () => {
      try {
        const {
          data: roleData
        } = await supabaseAdmin.from('roles').select('*').eq('id', user.rolId).single();
        if (roleData) {
          const newPermisos = roleData.permisos as Permissions;
          const currentStr = JSON.stringify(user.permisos);
          const newStr = JSON.stringify(newPermisos);
          if (currentStr !== newStr) {
            setUser(prev => prev ? {
              ...prev,
              permisos: newPermisos,
              rolNombre: roleData.nombre
            } : null);
          }
        }
      } catch (err) {
        console.error('Error refreshing user permissions:', err);
      }
    };
    // Listen for realtime changes dispatched by AppContext's roles/profiles channels
    const handleChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.table === 'roles' || detail?.table === 'profiles') {
        refreshCurrentUserPermissions();
      }
    };
    window.addEventListener('supabase-change', handleChange);
    return () => {
      window.removeEventListener('supabase-change', handleChange);
    };
  }, [user?.id, user?.rolId]);
  const login = useCallback(async (emailOrId: string, password?: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      // Determinar si es email o ID
      const isEmail = emailOrId.includes('@');
      let userData: any = null;
      let error: any = null;
      if (isEmail) {
        // Buscar por email
        const res = await supabaseAdmin.from('profiles').select('*').eq('email', emailOrId.trim().toLowerCase()).single();
        userData = res.data;
        error = res.error;
      } else {
        // Buscar por ID (para restaurar sesión)
        const res = await supabaseAdmin.from('profiles').select('*').eq('id', emailOrId).single();
        userData = res.data;
        error = res.error;
      }
      if (error || !userData) {
        return {
          success: false,
          error: 'Correo electrónico no encontrado'
        };
      }
      if (!userData.activo) {
        return {
          success: false,
          error: 'Usuario desactivado'
        };
      }
      if (userData.password && userData.password !== password) {
        return {
          success: false,
          error: 'Contraseña incorrecta'
        };
      }
      const {
        data: roleData
      } = await supabaseAdmin.from('roles').select('*').eq('id', userData.rol_id).single();
      const roleMap = {
        [userData.rol_id]: roleData
      };
      const userObj = mapProfile(userData, roleMap);
      setUser(userObj);
      localStorage.setItem('ventastrack_user_id', userObj.id);
      // Cache user for instant restore on next page load
      localStorage.setItem('ventastrack_user_cache', JSON.stringify(userObj));
      // Sync avatar from DB to localStorage so Header shows it immediately
      if (userData.avatar_url) {
        localStorage.setItem(`ventastrack_avatar_${userObj.id}`, userData.avatar_url);
      }
      window.dispatchEvent(new Event('avatar-updated'));
      (async () => {
        try {
          const location = await getCurrentLocation();
          await supabaseAdmin.from('activity_logs').insert({
            user_id: userObj.id,
            user_name: userObj.nombre,
            action: 'Inicio de sesión',
            location: location ? {
              lat: location.lat,
              lng: location.lng,
              timestamp: location.timestamp.toISOString()
            } : null,
            details: 'Acceso con correo y contraseña'
          });
          loadActivityLogs(userObj.id, userObj.rolNombre === 'Administrador').catch(() => {});
        } catch (_) {}
      })();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: 'Error de conexión'
      };
    }
  }, []);
  const logout = useCallback(async () => {
    if (user) {
      const location = await getCurrentLocation();
      await supabaseAdmin.from('activity_logs').insert({
        user_id: user.id,
        user_name: user.nombre,
        action: 'Cierre de sesión',
        location: location ? {
          lat: location.lat,
          lng: location.lng,
          timestamp: location.timestamp.toISOString()
        } : null,
        details: 'Salida del sistema'
      });
    }
    setUser(null);
    localStorage.removeItem('ventastrack_user_id');
    localStorage.removeItem('ventastrack_user_cache');
  }, [user]);
  const addActivityLog = useCallback(async (action: string, details: string) => {
    if (!user) return;
    const location = await getCurrentLocation();
    const {
      data
    } = await supabaseAdmin.from('activity_logs').insert({
      user_id: user.id,
      user_name: user.nombre,
      action,
      location: location ? {
        lat: location.lat,
        lng: location.lng,
        timestamp: location.timestamp.toISOString()
      } : null,
      details
    }).select().single();
    if (data) {
      setActivityLogs(prev => [{
        id: data.id,
        userId: data.user_id,
        userName: data.user_name,
        action: data.action,
        timestamp: new Date(data.timestamp),
        location: data.location ? {
          ...data.location,
          timestamp: new Date(data.location.timestamp)
        } : null,
        details: data.details
      }, ...prev]);
    }
  }, [user]);
  // Activity logs refresh on page navigation / manual refresh — no realtime channel needed
  // This saves a database connection on the Supabase pool
  const addNotification = useCallback((type: AppNotification['type'], title: string, message: string) => {
    const notif: AppNotification = {
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type,
      title,
      message,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => {
      const updated = [notif, ...prev].slice(0, 50);
      localStorage.setItem(NOTIFS_STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifications-updated'));
      return updated;
    });
  }, []);
  const markNotificationsRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({
        ...n,
        read: true
      }));
      localStorage.setItem(NOTIFS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);
  const handleSessionRestart = () => {
    setSessionExpired(false);
    window.location.reload();
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    availableUsers,
    login,
    logout,
    activityLogs,
    addActivityLog,
    notifications,
    addNotification,
    markNotificationsRead,
    isLoading,
    refreshUsers: loadUsersAndRoles
  }} data-id="element-301">
      {children}

      {/* Session Expired Modal */}
      {sessionExpired && <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-302">
          <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-sm w-full text-center" style={{
        animation: 'sessionExpiredIn 0.3s ease-out'
      }} data-id="element-303">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-50 flex items-center justify-center" data-id="element-304">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-305">
                <circle cx="12" cy="12" r="10" data-id="element-306" />
                <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" data-id="element-307" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-308">
              Sesión Expirada
            </h2>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed" data-id="element-309">
              Tu sesión expiró por inactividad. Por favor, inicia sesión
              nuevamente para continuar.
            </p>
            <button onClick={handleSessionRestart} className="w-full py-3.5 rounded-xl text-white font-bold text-base transition-all hover:opacity-90 active:scale-[0.98]" style={{
          background: 'linear-gradient(135deg, #1E3D96, #2952C4, #3B65D7)'
        }} data-id="element-310">
              Iniciar Sesión de Nuevo
            </button>
          </div>
          <style data-id="element-311">{`
            @keyframes sessionExpiredIn {
              from { opacity: 0; transform: scale(0.9) translateY(10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}